import { useState, useEffect } from 'react';
import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { catalogAdmin, catalog } from '../api/index.js';
import { Badge, statusBadge } from '../components/ui/badge.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card.js';
import { Button } from '../components/ui/button.js';
import { Loader2, AlertCircle, Pencil, X, Check, FolderOpen } from 'lucide-react';
import { SearchableSelect } from '../components/SearchableSelect.js';
import { format } from 'date-fns';
import { useEnableEdit } from '../hooks/useAuth.js';

const SENTINEL = 'ig.field.type.delete';

function sv(v: unknown): string {
  if (!v || v === SENTINEL) return '';
  return String(v);
}

function fmtDate(v: unknown): string {
  const s = sv(v);
  if (!s) return '';
  try { return format(new Date(s), 'MMM d, yyyy'); } catch { return s; }
}

function normalizeId(v: unknown): string {
  if (typeof v === 'string') return v;
  if (v && typeof v === 'object') {
    const o = v as any;
    return o.$oid ?? o._id ?? String(v);
  }
  return String(v ?? '');
}

const INPUT_CLS = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500';

const GROUP_STATUS_OPTIONS = [
  { value: '', label: '— None —' },
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'archived', label: 'Archived' },
];

function Row({ label, value }: { label: string; value?: React.ReactNode }) {
  if (value === undefined || value === null || value === '') return null;
  return (
    <div className="flex gap-4 py-1.5 text-sm border-b border-gray-50 last:border-0">
      <span className="w-44 text-gray-500 flex-shrink-0">{label}</span>
      <span className="text-gray-900 break-words min-w-0">{value}</span>
    </div>
  );
}

interface GroupForm {
  name: string;
  slug: string;
  status: string;
  releaseAt: string;
  originalReleaseAt: string;
  preorderReleaseAt: string;
  showReleaseDateOnPreorder: boolean;
  buyAllPrice: string;
  isArchived: boolean;
}

export function GroupDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canEdit = useEnableEdit();
  const [editing, setEditing] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveError, setSaveError] = useState('');
  const [form, setForm] = useState<GroupForm>({
    name: '', slug: '', status: '', releaseAt: '', originalReleaseAt: '',
    preorderReleaseAt: '', showReleaseDateOnPreorder: false,
    buyAllPrice: '', isArchived: false,
  });

  const { data: group, isLoading, error } = useQuery({
    queryKey: ['group-detail', id],
    queryFn: () => catalogAdmin.groupDetail(id!),
    enabled: !!id,
  });

  const seriesId = group ? normalizeId((group as any).seriesId) : '';
  const { data: seriesData } = useQuery({
    queryKey: ['catalog-series'],
    queryFn: catalog.series,
    staleTime: 10 * 60 * 1000,
    enabled: !!seriesId,
  });
  const seriesName = seriesId
    ? (seriesData?.series.find(s => s._id === seriesId)?.name ?? seriesId)
    : '';

  const { data: groupItemsData } = useQuery({
    queryKey: ['catalog-items-by-group', id],
    queryFn: () => catalog.items({ group: id!, limit: 20, sortBy: 'releaseAt', sortDir: 'asc' }),
    enabled: !!id && !!group,
    staleTime: 2 * 60 * 1000,
  });

  useEffect(() => {
    if (group) {
      setForm({
        name: sv(group.name),
        slug: sv(group.slug),
        status: sv((group as any).status),
        releaseAt: sv((group as any).releaseAt)?.slice(0, 16) ?? '',
        originalReleaseAt: sv((group as any).originalReleaseAt)?.slice(0, 16) ?? '',
        preorderReleaseAt: sv((group as any).preorderReleaseAt)?.slice(0, 16) ?? '',
        showReleaseDateOnPreorder: Boolean((group as any).showReleaseDateOnPreorder),
        buyAllPrice: sv((group as any).buyAllPrice),
        isArchived: Boolean((group as any).isArchived),
      });
    }
  }, [group]);

  async function handleSave() {
    setSaveStatus('saving');
    setSaveError('');
    try {
      const payload: Record<string, unknown> = {
        name: form.name,
        slug: form.slug,
        status: form.status,
        showReleaseDateOnPreorder: form.showReleaseDateOnPreorder,
        isArchived: form.isArchived,
      };
      if (form.releaseAt) payload.releaseAt = `${form.releaseAt}:00.000`;
      if (form.originalReleaseAt) payload.originalReleaseAt = `${form.originalReleaseAt}:00.000`;
      if (form.preorderReleaseAt) payload.preorderReleaseAt = `${form.preorderReleaseAt}:00.000`;
      if (form.buyAllPrice) payload.buyAllPrice = parseFloat(form.buyAllPrice);
      await catalogAdmin.updateGroup(id!, payload);
      await qc.invalidateQueries({ queryKey: ['group-detail', id] });
      await qc.invalidateQueries({ queryKey: ['catalog-groups'] });
      setSaveStatus('saved');
      setEditing(false);
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (err: unknown) {
      setSaveStatus('error');
      setSaveError(err instanceof Error ? err.message : 'Unknown error');
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400">
        <Loader2 className="animate-spin mr-2" /> Loading…
      </div>
    );
  }

  if (error || !group) {
    return (
      <div className="p-8 flex items-center gap-3 text-red-600">
        <AlertCircle size={20} />
        <p>{error instanceof Error ? error.message : 'Group not found'}</p>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-2xl mx-auto space-y-6">
      <div>
        <Link to="/groups" className="text-sm text-brand-600 hover:underline mb-2 inline-block">← Back to Groupings</Link>
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
            <FolderOpen size={22} className="text-gray-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold text-gray-900">{sv(group.name) || '(no name)'}</h1>
            <div className="flex items-center gap-3 mt-0.5">
              {(group as any).status && (
                <Badge variant={statusBadge(sv((group as any).status))}>{sv((group as any).status)}</Badge>
              )}
              <span className="text-xs text-gray-400 font-mono">{id}</span>
            </div>
          </div>
          {!editing && canEdit && (
            <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
              <Pencil size={14} /> Edit
            </Button>
          )}
        </div>
      </div>

      {editing ? (
        <Card>
          <CardHeader><CardTitle>Edit Grouping</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input className={INPUT_CLS} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
                <input className={INPUT_CLS} value={form.slug} onChange={e => setForm(f => ({ ...f, slug: e.target.value }))} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <SearchableSelect
                  value={form.status}
                  onChange={(v) => setForm((f) => ({ ...f, status: v }))}
                  options={GROUP_STATUS_OPTIONS}
                  placeholder="Status"
                  allowClear={false}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Buy All Price ($)</label>
                <input type="number" step="0.01" min="0" className={INPUT_CLS} value={form.buyAllPrice}
                  onChange={e => setForm(f => ({ ...f, buyAllPrice: e.target.value }))} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Release Date</label>
                <input type="datetime-local" className={INPUT_CLS} value={form.releaseAt}
                  onChange={e => setForm(f => ({ ...f, releaseAt: e.target.value }))} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Original Release</label>
                <input type="datetime-local" className={INPUT_CLS} value={form.originalReleaseAt}
                  onChange={e => setForm(f => ({ ...f, originalReleaseAt: e.target.value }))} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Preorder Release</label>
                <input type="datetime-local" className={INPUT_CLS} value={form.preorderReleaseAt}
                  onChange={e => setForm(f => ({ ...f, preorderReleaseAt: e.target.value }))} />
              </div>
            </div>
            <div className="flex gap-6 flex-wrap">
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input type="checkbox" checked={form.showReleaseDateOnPreorder}
                  onChange={e => setForm(f => ({ ...f, showReleaseDateOnPreorder: e.target.checked }))}
                  className="rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
                <span className="font-medium text-gray-700">Show release date on preorder</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input type="checkbox" checked={form.isArchived}
                  onChange={e => setForm(f => ({ ...f, isArchived: e.target.checked }))}
                  className="rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
                <span className="font-medium text-gray-700">Archived</span>
              </label>
            </div>
            {saveStatus === 'error' && <p className="text-sm text-red-600">{saveError}</p>}
            <div className="flex gap-2 justify-end">
              <Button variant="secondary" onClick={() => { setEditing(false); setSaveStatus('idle'); }}>
                <X size={14} /> Cancel
              </Button>
              <Button onClick={handleSave} loading={saveStatus === 'saving'}>
                <Check size={14} /> Save
              </Button>
            </div>
            {saveStatus === 'saved' && <p className="text-sm text-green-600 text-right">Saved ✓</p>}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader><CardTitle>Details</CardTitle></CardHeader>
          <CardContent className="space-y-0">
            <Row label="Name" value={sv(group.name)} />
            <Row label="Slug" value={sv(group.slug)} />
            <Row label="Status" value={sv((group as any).status)} />
            <Row label="Series" value={seriesName && seriesId
              ? <Link to={`/series/${seriesId}`} className="text-brand-600 hover:underline">{seriesName}</Link>
              : seriesName}
            />
            <Row label="Release Date" value={fmtDate((group as any).releaseAt)} />
            <Row label="Original Release" value={fmtDate((group as any).originalReleaseAt)} />
            <Row label="Preorder Release" value={fmtDate((group as any).preorderReleaseAt)} />
            <Row label="Show release date on preorder" value={(group as any).showReleaseDateOnPreorder ? 'Yes' : ''} />
            <Row label="Buy All Price" value={sv((group as any).buyAllPrice) ? `$${sv((group as any).buyAllPrice)}` : ''} />
            <Row label="Archived" value={(group as any).isArchived ? 'Yes' : ''} />
            <Row label="Created" value={fmtDate((group as any).createdAt)} />
            <Row label="Updated" value={fmtDate((group as any).updatedAt)} />
          </CardContent>
        </Card>
      )}

      {/* Items in this group */}
      {(() => {
        const items = groupItemsData?.items ?? [];
        const total = groupItemsData?.total ?? 0;
        if (total === 0) return null;
        return (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Items ({total})</CardTitle>
                <Link
                  to={`/catalog?group=${id}`}
                  className="text-xs text-brand-600 hover:underline"
                >
                  View all →
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left text-xs font-medium text-gray-400 uppercase pb-2">Title</th>
                      <th className="text-left text-xs font-medium text-gray-400 uppercase pb-2">Status</th>
                      <th className="text-left text-xs font-medium text-gray-400 uppercase pb-2">Release</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {items.map(item => {
                      const rel = item.releaseAt ? (() => { try { return format(new Date(item.releaseAt as string), 'MMM d, yyyy'); } catch { return item.releaseAt as string; } })() : '';
                      return (
                        <tr
                          key={item._id}
                          onClick={() => navigate(`/catalog/${item._id}`)}
                          className="hover:bg-gray-50 cursor-pointer transition-colors"
                        >
                          <td className="py-2 pr-4 font-medium text-gray-900 truncate max-w-[14rem]">{item.name}</td>
                          <td className="py-2 pr-4">
                            {item.status ? <Badge variant={statusBadge(item.status)}>{item.status}</Badge> : '—'}
                          </td>
                          <td className="py-2 text-gray-500 text-xs whitespace-nowrap">{rel || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {total > 20 && (
                <p className="text-xs text-gray-400 mt-3">Showing 20 of {total}. <Link to={`/catalog?group=${id}`} className="text-brand-600 hover:underline">View all →</Link></p>
              )}
            </CardContent>
          </Card>
        );
      })()}

      {/* Series breadcrumb */}
      {seriesId && seriesName && (
        <div className="text-sm">
          <Link to={`/series/${seriesId}`} className="text-brand-600 hover:underline">
            ← Back to Series: {seriesName}
          </Link>
        </div>
      )}
    </div>
  );
}
